import { Link, useLocation as useRouterLocation } from 'react-router-dom';
import { IconArrowRight as ArrowRight, IconPlus as Plus } from '@tabler/icons-react';
import Instagram from "@/icons/Instagram";
import DarkLightSwitch from './DarkLightSwitch';

// Helper function to get site name from environment
const getSiteName = () => {
    return (import.meta as any).env?.VITE_SITE_NAME || 'EventUp';
};

const columns: { title: string; links: { label: string; href: string }[] }[] = [
    {
        title: 'Discover',
        links: [
            { label: 'Browse events', href: '/search' },
            { label: 'Cities', href: '/search?browse=cities' },
            { label: 'Organizations', href: '/search?browse=organizers' },
            { label: 'Find my tickets', href: '/order' },
        ],
    },
    {
        title: 'Organizers',
        links: [
            { label: 'Create an event', href: '/organizations/events/create' },
            { label: 'Organizer dashboard', href: '/organizations' },
            { label: 'Promote an event', href: '/promote-event' },
            { label: 'About us', href: '/aboutus' },
        ],
    },
    {
        title: 'Support',
        links: [
            { label: 'Help Center', href: '/help' },
            { label: 'Contact Us', href: '/contact' },
        ],
    },
];

const legalLinks = [
    { label: 'Privacy Policy', href: '/privacy-policy' },
    { label: 'Terms of Service', href: '/terms-conditions' },
    { label: 'Cookie Policy', href: '/cookie-policy' },
];

const Footer = ({ hasMobileNav = false }: { hasMobileNav?: boolean } = {}) => {
    const routerLocation = useRouterLocation();
    const isOrganizationPage = routerLocation.pathname.startsWith('/organizations');
    const isSearchPage = routerLocation.pathname.startsWith('/search');
    const linkClass = "text-[15px] font-medium text-grey30 hover:text-grey10 dark:text-nightMuted dark:hover:text-white transition-colors";
    const copyright = `© ${new Date().getFullYear()} ${getSiteName()} LLC. All rights reserved.`;

    if (isOrganizationPage) {
        return (
            <footer className="w-full md:fixed md:bottom-0 md:left-0 md:right-0 md:z-30 bg-white dark:bg-nightBg border-t border-black/[0.06] dark:border-white/[0.08] py-4 px-4 transition-colors">
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div className="flex flex-col gap-4 md:flex-row md:flex-wrap md:items-center md:gap-6">
                        <Link to="/help" className={linkClass}>Help Center</Link>
                        <Link to="/contact" className={linkClass}>Contact Us</Link>
                        {legalLinks.map(link => (
                            <Link key={link.href} to={link.href} className={linkClass}>{link.label}</Link>
                        ))}
                        <DarkLightSwitch />
                    </div>
                    <div className="text-sm text-grey50 dark:text-nightMuted">{copyright}</div>
                </div>
            </footer>
        );
    }

    return (
        <footer className={`relative isolate w-full overflow-hidden bg-white dark:bg-nightBg border-t border-black/[0.06] dark:border-white/[0.08] ${hasMobileNav ? 'pb-28 sm:pb-0' : ''} transition-colors`}>
            <div className="mx-auto w-full max-w-[1200px] px-4 pt-16 sm:pt-24 xl:px-0">
                <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
                    <h2 className="max-w-[640px] text-[clamp(40px,6vw,80px)] font-bold leading-[0.95] tracking-[-0.05em] text-grey10 dark:text-white">
                        Don't miss <span className="text-primary dark:text-nightAccent">what's next.</span>
                    </h2>
                    <div className="flex flex-col gap-3 sm:flex-row">
                        {!isSearchPage && (
                            <Link to="/search" className="inline-flex h-14 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-full bg-grey10 px-7 text-base font-semibold text-white transition-colors hover:bg-black dark:bg-white dark:text-nightBg dark:hover:bg-grey90">
                                Browse events <ArrowRight className="h-4 w-4" />
                            </Link>
                        )}
                        <Link to="/organizations/events/create" className="inline-flex h-14 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-full px-7 text-base font-semibold text-grey10 ring-1 ring-grey90 transition-colors hover:bg-grey99 dark:text-white dark:ring-white/15 dark:hover:bg-white/[0.06]">
                            List your event <Plus className="h-4 w-4" />
                        </Link>
                    </div>
                </div>

                <div className="mt-16 grid grid-cols-2 gap-x-6 gap-y-10 border-t border-black/[0.06] pt-12 dark:border-white/[0.08] md:grid-cols-4">
                    {columns.map(column => (
                        <div key={column.title}>
                            <div className="mb-5 text-xs font-bold uppercase tracking-[0.16em] text-grey50 dark:text-white/40">{column.title}</div>
                            <ul className="flex flex-col gap-3">
                                {column.links.map(link => (
                                    <li key={link.href}>
                                        <Link to={link.href} className={linkClass}>{link.label}</Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                    <div>
                        <div className="mb-5 text-xs font-bold uppercase tracking-[0.16em] text-grey50 dark:text-white/40">Contact</div>
                        <ul className="flex flex-col gap-3">
                            <li><a href="mailto:admin@alimotech.com" className={linkClass}>admin@alimotech.com</a></li>
                            <li><a href="tel:9494316160" className={linkClass}>(949) 431-6160</a></li>
                            <li className="text-[15px] text-grey40 dark:text-nightMuted">Alimotech LLC<br />Aliso Viejo, CA 92656</li>
                        </ul>
                        <a
                            href="https://www.instagram.com/eventup.ai/"
                            target="_blank"
                            rel="noreferrer"
                            aria-label="Instagram"
                            className="mt-5 inline-flex h-11 w-11 items-center justify-center rounded-full bg-grey99 text-grey20 transition-colors hover:bg-grey10 hover:text-white dark:bg-white/[0.06] dark:text-nightTextSecondary dark:hover:bg-white dark:hover:text-nightBg"
                        >
                            <Instagram width={18} height={18} color="currentColor" />
                        </a>
                    </div>
                </div>

                <div className="mt-14 flex flex-col-reverse gap-5 md:flex-row md:items-center md:justify-between">
                    <div className="text-sm text-grey50 dark:text-nightMuted">{copyright}</div>
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                        {legalLinks.map(link => (
                            <Link key={link.href} to={link.href} className="text-sm font-medium text-grey40 hover:text-grey10 dark:text-nightMuted dark:hover:text-white">{link.label}</Link>
                        ))}
                        <DarkLightSwitch />
                    </div>
                </div>
            </div>

            <div aria-hidden className="pointer-events-none mt-10 select-none overflow-hidden">
                <div className="translate-y-[18%] whitespace-nowrap text-center text-[clamp(96px,23vw,340px)] font-bold leading-[0.8] tracking-[-0.07em] bg-gradient-to-b from-grey10 to-grey10/0 bg-clip-text text-transparent dark:from-white dark:to-white/0">
                    {getSiteName().toLowerCase()}
                </div>
            </div>
        </footer>
    );
};

export default Footer;
